import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@trillion/ui/components/ui/dialog";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import { Label } from "@trillion/ui/components/ui/label";
import { Switch } from "@trillion/ui/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@trillion/ui/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { firstFieldErrors, zodToFieldErrors } from "@trillion/validation";
import { createSettingsRequestSchema } from "@/validation/create-settings-request";
import { updateSettingsRequestSchema } from "@/validation/update-settings-request";
import { jobsApi, settingsApi } from "@/lib/admin-api";
import type { SettingView } from "@/lib/settings-types";
import { SecretValueInput } from "@trillion/ui/components/admin/secret-value-input";

interface SettingsDialogProps {
  /** undefined → create mode; present → edit mode (scope + key are read-only, value-only update). */
  editing?: SettingView;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Refetch the list after a successful create/update. */
  onSaved: () => void;
  /**
   * Create mode only (sc-114201): the new setting's fixed scope, e.g. `job:heartbeat`. The scope renders
   * read-only and the recurring-job suggestions are not fetched. Ignored in edit mode.
   */
  presetScope?: string;
}

type ScopeKind = "global" | "job";

// Single create/edit dialog. Create: scope selector (global vs job + job-id), key, value, is_secret.
// Edit: scope + key fixed (value-only PUT, per sc-94854); is_secret preserved — a plain setting becomes a
// secret only through the row menu's one-way "Convert to secret" (sc-116657). Secret values are
// write-only — never seeded from the stored secret (which the API never returns).
export function SettingsDialog({
  editing,
  open,
  onOpenChange,
  onSaved,
  presetScope,
}: SettingsDialogProps) {
  const isEdit = editing !== undefined;
  const isPreset = !isEdit && presetScope !== undefined;
  const [scopeKind, setScopeKind] = useState<ScopeKind>("global");
  const [jobId, setJobId] = useState("");
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const [isSecret, setIsSecret] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  // Registered recurring-job ids, suggested (not enforced) for the Job-level scope id. Free-text is always
  // allowed so a setting can be pre-provisioned for a not-yet-registered job (epic 🧭 2026-06-28).
  const [jobIds, setJobIds] = useState<string[]>([]);

  // Seed the form when the dialog opens: create → blank; edit → from the row, EXCEPT a secret
  // value which is never seeded (write-only — the API returns value:null for secrets).
  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (editing) {
      const isJob = editing.scope.startsWith("job:");
      setScopeKind(isJob ? "job" : "global");
      setJobId(isJob ? editing.scope.slice("job:".length) : "");
      setKey(editing.key);
      setIsSecret(editing.is_secret);
      setValue(editing.is_secret ? "" : (editing.value ?? ""));
    } else {
      setScopeKind("global");
      setJobId("");
      setKey("");
      setIsSecret(false);
      setValue("");
    }
  }, [open, editing]);

  // Load registered recurring-job ids to suggest in the Job-level scope field (create mode only — edit
  // scope is read-only). A fetch failure degrades to free-text-only (empty suggestions, no blocking toast):
  // suggestions are a convenience, never a gate on entry. The `cancelled` guard avoids a setState after a
  // mid-flight close.
  useEffect(() => {
    if (!open || isEdit || isPreset) return;
    let cancelled = false;
    jobsApi
      .recurring()
      .then((jobs) => {
        if (!cancelled) setJobIds(jobs.map((j) => j.id));
      })
      .catch(() => {
        if (!cancelled) setJobIds([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isEdit, isPreset]);

  const clearError = (field: string) => setErrors((e) => ({ ...e, [field]: "" }));

  // Clear all form state — including any entered secret plaintext — so it never lingers in
  // component memory after the dialog closes. Routed through every close path (save, cancel,
  // escape/outside-click) to keep the write-only secret UX tight.
  function resetForm() {
    setScopeKind("global");
    setJobId("");
    setKey("");
    setValue("");
    setIsSecret(false);
    setErrors({});
  }

  function handleClose() {
    resetForm();
    onOpenChange(false);
  }

  async function handleSubmit() {
    if (isEdit && editing) {
      const result = updateSettingsRequestSchema.safeParse({ value });
      if (!result.success) {
        setErrors(firstFieldErrors(zodToFieldErrors(result.error)));
        return;
      }
      setSubmitting(true);
      try {
        await settingsApi.update(editing.scope, editing.key, result.data.value);
        toast.success(`Updated "${editing.key}"`);
        handleClose();
        onSaved();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to update setting");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Create — assemble the derived scope and validate the full body.
    const scope = isPreset
      ? presetScope!
      : scopeKind === "job"
        ? `job:${jobId.trim()}`
        : "global";
    const result = createSettingsRequestSchema.safeParse({ scope, key, value, is_secret: isSecret });
    if (!result.success) {
      setErrors(firstFieldErrors(zodToFieldErrors(result.error)));
      return;
    }
    setSubmitting(true);
    try {
      await settingsApi.create(result.data);
      toast.success(`Created "${result.data.key}"`);
      handleClose();
      onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create setting");
    } finally {
      setSubmitting(false);
    }
  }

  const secretValue = isEdit ? editing!.is_secret : isSecret;

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : handleClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit setting" : "Add setting"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the value. Scope and key are fixed. To make a plain setting secret, use Convert to secret in its row menu."
              : isPreset
                ? `Create a setting in ${presetScope}.`
                : "Create a global or job-level conduit setting."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Scope */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="scope-kind">Scope</Label>
            {isEdit || isPreset ? (
              <>
                <Input id="scope-kind" value={isEdit ? editing!.scope : presetScope!} disabled />
                {/* A preset the scope schema rejects still says why Create did nothing. */}
                {isPreset && errors.scope && <p className="text-xs text-destructive">{errors.scope}</p>}
              </>
            ) : (
              <>
                <Select
                  value={scopeKind}
                  onValueChange={(v) => {
                    setScopeKind(v as ScopeKind);
                    clearError("scope");
                  }}
                >
                  <SelectTrigger id="scope-kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Global</SelectItem>
                    <SelectItem value="job">Job-level</SelectItem>
                  </SelectContent>
                </Select>
                {scopeKind === "job" && (
                  <>
                    <Input
                      id="job-id"
                      list="job-id-options"
                      value={jobId}
                      placeholder="recurring-job-id (e.g. netacuity-mmdb)"
                      onChange={(e) => {
                        setJobId(e.target.value);
                        clearError("scope");
                      }}
                    />
                    {/* Suggest registered recurring jobs; free-text entry stays allowed (pre-provisioning). */}
                    <datalist id="job-id-options">
                      {jobIds.map((id) => (
                        <option key={id} value={id} />
                      ))}
                    </datalist>
                  </>
                )}
                {/* `scope` is a derived field (scope-kind + job-id); its Zod error renders here. */}
                {errors.scope && <p className="text-xs text-destructive">{errors.scope}</p>}
              </>
            )}
          </div>

          {/* Key */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="key">Key</Label>
            <Input
              id="key"
              value={key}
              placeholder="setting_key"
              disabled={isEdit}
              onChange={(e) => {
                setKey(e.target.value);
                clearError("key");
              }}
            />
            {errors.key && <p className="text-xs text-destructive">{errors.key}</p>}
          </div>

          {/* is_secret toggle — create only (value-only update preserves it on edit). */}
          {!isEdit && (
            <div className="flex items-center justify-between">
              <Label htmlFor="is-secret">Secret (encrypted at rest)</Label>
              <Switch id="is-secret" checked={isSecret} onCheckedChange={setIsSecret} />
            </div>
          )}

          {/* Value */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="value">Value</Label>
            {secretValue ? (
              <SecretValueInput
                id="value"
                value={value}
                placeholder={isEdit ? "•••• set — enter a new value to replace" : "secret value"}
                onChange={(v) => {
                  setValue(v);
                  clearError("value");
                }}
              />
            ) : (
              <Input
                id="value"
                value={value}
                placeholder="value"
                onChange={(e) => {
                  setValue(e.target.value);
                  clearError("value");
                }}
              />
            )}
            {isEdit && editing!.is_secret && (
              <p className="text-xs text-muted-foreground">
                The current secret is never displayed. Entering a value replaces it.
              </p>
            )}
            {errors.value && <p className="text-xs text-destructive">{errors.value}</p>}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting && <Loader2 className="size-4 animate-spin" />}
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
